import React from "react";
import { FolderProperties, Folders, folderState, MyPaper, TreeItemLoader } from "../../components";
import { Alert, AlertTitle, Box, Fab, Grid2 as Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useDeleteFolderMutation, useLazyGetFoldersQuery } from "../../redux/services";
import { Add, DeleteOutlined, EditOutlined, Folder, InfoOutlined } from "@mui/icons-material";
import { useAppDispatch } from "../../redux";
import { openDialog } from "../../redux/slices/dialog";
import { AddEditFolder } from "../../components/folders/addFolder";
import { closeConfirmationDialog, openConfirmationDialog, updateCongirmationDialogBody } from "../../redux/slices/confirmationDialog";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { enqueueSnackbar } from "notistack";

export interface FolderTemplateProps {
    hasAccess: boolean
}

export const FolderTemplates: React.FC<FolderTemplateProps> = (props) => {

    const dispatch = useAppDispatch();

    const [fetchFolders] = useLazyGetFoldersQuery();
    const [deleteFolder] = useDeleteFolderMutation();

    const [templates, setTemplates] = React.useState<folderState[]>([]);
    const [rootTemplate, setRootTemplate] = React.useState<folderState>();
    const [selectedTemplate, setSelectedTemplate] = React.useState<folderState>();
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | HTMLLIElement | null>(null);
    const [isSuccess, setSuccess] = React.useState<boolean>(false);
    const [isLoading, setLoading] = React.useState<boolean>(false);

    const fetchTemplateFolders = async () => {
        let currenTemplate = { ...selectedTemplate };

        setSelectedTemplate(undefined);

        setSuccess(false);
        setLoading(true);

        const { data: templateParent } = await fetchFolders({ sequence_id: 'F-000002' }).unwrap();

        if (Array.isArray(templateParent)) {
            setRootTemplate(templateParent[0]);
            const allTemplates = await fetchFolders({ 
                parent_object_id: templateParent[0]._id,
                parent_object_type:'folder',
                sort_by: 'folder_name', 
                sort_order: 'asc',
                page_size: 10000
            }).unwrap();

            if (Array.isArray(allTemplates?.data ?? [])) {
                setTemplates(allTemplates?.data ?? []);
                setSuccess(true);

                if (allTemplates?.data?.length) {
                    setSelectedTemplate(allTemplates?.data?.find(a => a?._id === currenTemplate?._id) || allTemplates?.data?.[0]);
                }
            }
        }

        setLoading(false);
    };

    const handleClick = (template: folderState) => {
        setSelectedTemplate(template);
    };

    const handleRightClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLLIElement, MouseEvent>, template: folderState) => {
        event.preventDefault();
        setSelectedTemplate(template);
        setAnchorEl(event.currentTarget);
    };

    const addEditTemplate = (template?: folderState) => {
        const isEdit = template ? true : false;
        dispatch(openDialog({
            title: `${isEdit ? "Update" : "Create"} Template`,
            hideCloseIcon: true,
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            body: <AddEditFolder
                parentFolder={rootTemplate ? { ...rootTemplate, folder_name: 'Template' } : undefined}
                folder={template}
                isEdit={isEdit}
                history={[]}
                refetch={(_state) => {
                    fetchTemplateFolders();
                }}
                variant="template"
            />
        }))
    };

    const showProperties = (folderDetails?: folderState) => {
        if (folderDetails) {
            dispatch(openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                enablePadding: false,
                body: <FolderProperties {...folderDetails} />
            }))
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteFolder = () => {
        dispatch(openConfirmationDialog({
            title: "Are you sure?",
            body: `Do you want to delete the folder template: ${selectedTemplate?.folder_name}?`,
            positiveBtn: 'Delete',
            negativeBtn: 'Cancel',
            onOk: () => confirmDeleteFolderDelete(),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    const confirmDeleteFolderDelete = async () => {
        dispatch(closeConfirmationDialog());

        dispatch(openBackdrop('Deleting folder...'));

        await deleteFolder({ id: selectedTemplate?._id }).then((res: any) => {
            if (res.error) {
                let errMessage = res.error?.data?.title ?? 'Something went wrong, Unable to delete folder template.';
                enqueueSnackbar(errMessage, { variant: "error" });
                dispatch(updateCongirmationDialogBody(<Stack>
                    <Typography gutterBottom variant="body2">{`Do you want to delete the folder: ${selectedTemplate?.folder_name}?`}</Typography>

                    <Alert variant="outlined" severity="warning">
                        <AlertTitle>{errMessage}</AlertTitle>
                    </Alert>
                </Stack>));
            } else {
                fetchTemplateFolders();
                enqueueSnackbar('Deleted the folder', { variant: "success" });
            }
        }).catch((err) => {
            enqueueSnackbar(err.data?.title ?? 'Something went wrong, Unable to delete folder.', { variant: "error" });
        });

        dispatch(closeBackdrop());
    };

    React.useEffect(() => {
        fetchTemplateFolders();
        // eslint-disable-next-line
    }, []);

    const open = Boolean(anchorEl);
    const id = open ? 'treeItem-' + selectedTemplate?._id : undefined;

    return <MyPaper height={"calc(100% - 6px)"} padding={0}>
        <Grid container sx={{ height: "100%" }}>
            <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }} sx={{
                padding: "8px",
                paddingTop: 0,
                paddingRight: "1px",
                height: "100%",
                '--Grid-borderWidth': '1px',
                borderRight: 'var(--Grid-borderWidth) solid',
                borderColor: 'divider',
                position: "relative",
            }}>

                {isLoading && <Box mt={1}>
                    <TreeItemLoader />
                </Box>}

                <List dense>
                    {templates?.map(template => {
                        const isSelected = template._id === selectedTemplate?._id;
                        return <ListItem key={template._id} disablePadding onContextMenu={(e) => handleRightClick(e, template)} sx={{ borderRight: (theme) => isSelected ? `5px solid ${theme.palette.primary.main}` : `` }}>
                            <ListItemButton selected={isSelected} onClick={() => handleClick(template)}>
                                <ListItemIcon sx={{ minWidth: "36px" }}>
                                    <Folder htmlColor="#ffbf66" />
                                </ListItemIcon>
                                <ListItemText sx={{ wordBreak: "break-word" }} primary={`${template?.folder_name ?? ""}`} />
                            </ListItemButton>
                        </ListItem>
                    })}
                </List>

                {isSuccess && templates.length === 0 && <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
                    <Typography variant="caption" sx={{ textAlign: "center" }}>You are yet to create a template, please click the below button to create one.</Typography>
                </Stack>}

                {/* Add Template Button */}
                <Box sx={{ position: "absolute", bottom: "8px", right: "8px" }}>
                    <Fab
                        size="small"
                        color="primary"
                        variant={"extended"}
                        onClick={() => addEditTemplate()}
                    >
                        <Add />
                        {"New Template"}
                    </Fab>
                </Box>
            </Grid>

            {selectedTemplate?.sequence_id && <Grid key={selectedTemplate?._id} size={{ xs: 12, sm: 9, md: 9, lg: 9, xl: 9 }} sx={{ height: "100%" }}>
                <Folders
                    currentItem={selectedTemplate}
                    showCreateBtn={true}
                    creatable={true}
                    updatable={true}
                    deletable={true}
                    readable={true}
                    publishable={true}
                    hideDocuments={true}
                    hideShadow={true}
                />
            </Grid>}

            <Menu
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                {[
                    { showIcon: true, icon: <EditOutlined color="primary" />, name: "Edit", onClick: () => addEditTemplate(selectedTemplate) },
                    { showIcon: true, icon: <DeleteOutlined color="error" />, name: "Delete", onClick: handleDeleteFolder },
                    { showIcon: true, icon: <InfoOutlined fontSize="small" color="primary" />, name: "Properties", onClick: () => showProperties(selectedTemplate ?? undefined) },
                ]?.map((option, index) => (<MenuItem key={index}
                    sx={{ borderTop: (theme) => [1, 2].includes(index) ? `1px solid ${theme.palette.divider}` : `` }}
                    onClick={() => {
                        option.onClick();
                        setAnchorEl(null);
                    }}>
                    {option.showIcon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText>{option.name}</ListItemText>
                </MenuItem>))}
            </Menu>
        </Grid>
    </MyPaper>
}